import { useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import useEntityTitles from "hooks/useEntityTitles";
import usePluginEntities from "hooks/usePluginEntities";
import { defaultOnError } from "util/conditional/onError";
import "./types";
const useInputDetails = (typedInputs) => {
  var _a;
  const regularIds = useMemo(() => typedInputs.filter((t) => t.type === "input").map((t) => t.id), [typedInputs]);
  const titleEntities = useMemo(() => regularIds.map((id) => ({ id, type: "inputs" })), [regularIds]);
  const {
    titlesById,
    isInitialLoading: isCatalogLoading,
    isFetching: isCatalogFetching,
    isError: isCatalogError
  } = useEntityTitles(titleEntities);
  const fetchers = (_a = usePluginEntities("inputDetailsFetchers")) != null ? _a : [];
  const nonDefaultIdsByType = useMemo(() => {
    const grouped = /* @__PURE__ */ new Map();
    typedInputs.forEach(({ id, type }) => {
      var _a2;
      if (type === "input") return;
      const existing = (_a2 = grouped.get(type)) != null ? _a2 : [];
      existing.push(id);
      grouped.set(type, existing);
    });
    return grouped;
  }, [typedInputs]);
  const enabled = nonDefaultIdsByType.size > 0;
  const queryKey = useMemo(
    () => [
      "input_details",
      Array.from(nonDefaultIdsByType.entries()).map(([type, ids]) => `${type}:${[...ids].sort().join(",")}`).sort()
    ],
    [nonDefaultIdsByType]
  );
  const {
    data: pluginResolved,
    isInitialLoading: isPluginLoading,
    isFetching: isPluginFetching,
    isError: isPluginError
  } = useQuery({
    queryKey,
    queryFn: () => defaultOnError(
      Promise.all(
        fetchers.map((fetcher) => {
          var _a2;
          const ids = (_a2 = nonDefaultIdsByType.get(fetcher.type)) != null ? _a2 : [];
          return ids.length > 0 ? fetcher.fetchDetails(ids) : Promise.resolve([]);
        })
      ).then((chunks) => chunks.flat()),
      "Loading input details failed with status",
      "Could not load input details"
    ),
    enabled,
    placeholderData: keepPreviousData
  });
  const resolvedById = useMemo(() => {
    const out = {};
    regularIds.forEach((id) => {
      const title = titlesById[id];
      if (title !== void 0) {
        out[id] = { type: "input", id, title };
      }
    });
    (pluginResolved != null ? pluginResolved : []).forEach((resolved) => {
      out[resolved.id] = resolved;
    });
    return out;
  }, [regularIds, titlesById, pluginResolved]);
  return {
    resolvedById,
    isInitialLoading: isCatalogLoading || isPluginLoading,
    isFetching: isCatalogFetching || isPluginFetching,
    isError: isCatalogError || isPluginError
  };
};
export default useInputDetails;
